import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SearchPage } from '@backstage/plugin-search';
import { VisitListener } from '@backstage/plugin-home';
import { toEntityRef, visitName } from './visits';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ExpandableNavigation,
  LightBox,
  ReportIssue,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { RevenexxDocsStyles } from './components/techdocs';
import {
  SettingsLayout,
  UserSettingsGeneral,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { AskPage } from '@revenexx/backstage-plugin-docs-assistant';
import { apis } from './apis';
import { revenexxDark, revenexxLight } from './theme/revenexxTheme';
import { RevenexxThemeProvider } from './theme/ThemeProvider';
import { CustomSignInPage } from './components/auth/SignInPage';
import { entityPage } from './components/catalog/EntityPage';
import { CatalogIndex } from './components/catalog/list/CatalogIndex';
import { HomePage } from './components/home/HomePage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { JamPage } from './components/jam/JamPage';
import { ResponsibilityMapPage } from './components/organisation';
import { AreaPage } from './components/areas';
import { McpPage } from './components/mcp/McpPage';
import { ScorecardsPage } from './components/scorecards/ScorecardsPage';
import { SecurityPage } from './components/security/SecurityPage';
import { DeploymentsPage } from './components/deployments/DeploymentsPage';
import { StatusPage } from './components/status/StatusPage';
import { AccessPage } from './components/access/AccessPage';
import { CreatePage, CreateTemplatePage } from './components/create';
import { ApiDocsPage } from './components/api-docs/ApiDocsPage';
import { DocsHubPage } from './components/docs/DocsHubPage';

import { AlertDisplay } from '@backstage/core-components';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { createApp } from '@backstage/app-defaults';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const app = createApp({
  // Light first, so it is the default a colleague lands on — the public
  // Developer Portal is light, and this portal is meant to read as the same
  // product (DX-275). Registering two themes also gives the Settings theme
  // picker something to choose between; with the single theme it had before,
  // it was a control with nothing behind it.
  themes: [
    {
      id: 'revenexx-light',
      title: 'revenexx Light',
      variant: 'light',
      Provider: ({ children }) => (
        <RevenexxThemeProvider theme={revenexxLight} isDark={false}>
          {children}
        </RevenexxThemeProvider>
      ),
    },
    {
      id: 'revenexx-dark',
      title: 'revenexx Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <RevenexxThemeProvider theme={revenexxDark} isDark>
          {children}
        </RevenexxThemeProvider>
      ),
    },
  ],
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      // Without this the catalog's own "Create" button has nowhere to go and
      // stays hidden, so the Create page would only be reachable from the
      // sidebar and the home page.
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    // Both are optional, and both are cards the Create page renders: an
    // unbound route leaves the reader with a link that goes nowhere.
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
  },
  components: {
    SignInPage: props => <CustomSignInPage {...props} />,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    {/*
      The catalog list (DX-305). The stock page stays the route's element,
      which is what mounts the catalog's own route for every plugin that links
      to it; the list is its outlet, and hands an address asking for another
      kind back to the stock page (CatalogIndex).
    */}
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CatalogIndex />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    {/*
      The docs hub in front of the reader (DX-306), and the list of every
      documented entity it replaced at /docs one level below it — the
      "Service docs" card leads there. /docs/sites has one segment, so it
      cannot be read as the reader's namespace/kind/name below.
    */}
    <Route path="/docs" element={<DocsHubPage />} />
    <Route path="/docs/sites" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      {/*
        All four contributed addons were already a dependency; only ReportIssue
        was wired (DX-275). ExpandableNavigation is the one that matters for
        "die Doku entsprechend aufbereitet": it gives the left-hand tree the
        collapsible sections the reference portal has.

        TextSize is not wired (DX-352). It is the only addon that lives in
        the reader's settings menu, and with it Backstage draws a row of its
        own above the document for one gear icon — which inside the frame
        read as a gap under the header. The browser's own zoom does what it
        did. Adding a settings addon back brings the row back with it.
      */}
      <TechDocsAddons>
        <RevenexxDocsStyles />
        <ExpandableNavigation />
        <ReportIssue />
        <LightBox />
      </TechDocsAddons>
    </Route>
    <Route path="/api-docs" element={<ApiDocsPage />} />
    <Route path="/api-explorer" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            EXPERIMENTAL_TemplateListPageComponent: CreatePage,
            EXPERIMENTAL_TemplateWizardPageComponent: CreateTemplatePage,
          }}
        />
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout>
        <SettingsLayout.Route path="general" title="General">
          <UserSettingsGeneral />
        </SettingsLayout.Route>
      </SettingsLayout>
    </Route>
    <Route path="/jam" element={<JamPage />} />
    {/*
      One landing page per field (DX-275). These replace the sidebar's old
      per-field links, which filtered the catalog on systems that do not exist
      and so always arrived empty.
    */}
    <Route
      path="/platform"
      element={<AreaPage field="Platform Engineering" />}
    />
    <Route path="/product" element={<AreaPage field="Product Engineering" />} />
    <Route path="/dx" element={<AreaPage field="Developer Experience" />} />
    <Route path="/consulting" element={<AreaPage field="Consulting" />} />
    <Route path="/verantwortung" element={<ResponsibilityMapPage />} />
    {/* Every repository's deploys of the week (DX-304; specs/deployments.md). */}
    <Route path="/deployments" element={<DeploymentsPage />} />
    {/*
      The internal view of status.revenexx.com (DX-309; specs/status-page.md).
    */}
    <Route path="/status" element={<StatusPage />} />
    {/*
      The pages the target picture's navigation led to before they were built
      (DX-302): every one of them now stands on its own issue's page.
    */}
    <Route path="/scorecards" element={<ScorecardsPage />} />
    <Route path="/security" element={<SecurityPage />} />
    <Route path="/access" element={<AccessPage />} />
    {/* The MCP servers and the local tools (DX-501; specs/mcp-and-cli.md). */}
    <Route path="/mcp" element={<McpPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    {/*
      Ask is the one place to put a question (DX-357): the Docs Assistant from
      PE-66 is its engine rather than a page beside it. Its old address still
      arrives, so a bookmark or a link in a doc keeps working.
    */}
    <Route path="/ask" element={<AskPage />} />
    <Route path="/docs-assistant" element={<Navigate to="/ask" replace />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <SignalsDisplay />
    <AppRouter>
      {/* Records each page a reader opens, which is what the palette's Recent
          and the home page's visit cards read (DX-349). Nothing did before:
          the history had a store and two readers and no writer, so both
          readers were always empty. Inside the router because it reads the
          location; given its two rules rather than left to its defaults,
          which it rebuilds on every render (visits.ts says why that
          matters). */}
      <VisitListener toEntityRef={toEntityRef} visitName={visitName} />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
